<template>
<div class="font-input">
    <LabelWithHelpText>
        <slot name="label" />
    </LabelWithHelpText>
    <p class="help">
        Select a default font.
    </p>
    <div class="field has-addons">
        <div class="control">
            <a @click="prevFont" class="button">
                <FaIcon icon="chevron-left"/>
            </a>
        </div>
        <div class="control">
            <span class="select">
                <select
                    :value="value || placeholder"
                    :style="urlToFontConfig(value).styleObj"
                    @change="updateFont($event.target.value)">
                    <option
                        v-for="font in fontConfigs"
                        :value="font.url"
                        :key="font.url"
                        :style="font.styleObj">
                        {{ font.name }}
                    </option>
                </select>
            </span>
        </div>
        <p class="control">
            <a @click="nextFont" class="button">
                <FaIcon icon="chevron-right"/>
            </a>
        </p>
    </div>
    <p class="help">
        or enter a
        <a href="https://fonts.google.com" target="_blank">font URL</a>.
        Here's a <a href="https://youtu.be/frfESHqHzCI" target="_blank">video tutorial</a>
        on how to add a font.
    </p>
    <div class="control is-expanded" :class="controlClass">
        <input
            v-bind="$attrs"
            v-on="inputListeners"
            :value="value || placeholder"
            type="url"
            class="input"/>
    </div>
    <p class="help" :class="fontState.class">
        {{ fontState.text }}
    </p>
</div>
</template>

<script>
import * as C from '@shared/constants'
import { isValidFontUrl, FONT_ORIGIN, DEFAULT_FONT_URL } from '@shared/utils'
import defaultFontUrls from './font-urls'
import { loadFont, familyParamToStyle } from './font-loader-utils'

export default {
    name: 'FontInput',
    inheritAttrs: false,
    props: {
        value: String,
        fontLoadingState: String
    },
    data: () => {
        return {
            fontOrigin: FONT_ORIGIN,
            placeholder: DEFAULT_FONT_URL,
            fontIndex: 0,
            fontConfigs: [] // should be obj of fontConfigs
        }
    },
    computed: {
        inputListeners: vm => ({
            ...vm.$listeners,
            // for transparent v-model with the parent component
            input: event => vm.$emit('input', event.target.value)
        }),
        controlClass: vm => ({
            'is-loading': vm.fontLoadingState === C.STATE_LOADING
        }),
        fontState: vm => {
            let text = ''
            let className = ''

            switch (vm.fontLoadingState) {
                case C.STATE_ERROR:
                    text = 'Font failed to load'
                    className = 'has-text-danger'
                    break
                default:
                    text = ''
            }

            if (vm.value && !isValidFontUrl(vm.value)) {
                className = 'has-text-danger'
                if (!vm.value.startsWith(vm.fontOrigin)) {
                    text = `The URL must start with "${vm.fontOrigin}"`
                } else {
                    text = 'Invalid URL'
                }
            }

            return { text, class: className }
        }
    },
    watch: {
        value: {
            // Give the <select> a value to render if user is using a non default font.
            handler (fontUrl) {
                if (!isValidFontUrl(fontUrl)) { return }

                if (!defaultFontUrls.includes(fontUrl)) {
                    defaultFontUrls.unshift(fontUrl)
                    this.loadFonts()
                }
                const idx = defaultFontUrls.indexOf(fontUrl)
                this.fontIndex = idx
            },
            immediate: true,
        }
    },
    created () {
        this.loadFonts()
    },
    methods: {
        loadFonts () {
            this.fontConfigs = defaultFontUrls.map(this.urlToFontConfig)
        },
        urlToFontConfig (url) {
            if (!url) { return {} }

            const urlObj = new URL(url)
            const familyParam = urlObj.searchParams.get('family')
            urlObj.searchParams.set('text', familyParam)
            loadFont(urlObj.href)

            const [family] = familyParam.split(/:|@/)
            const styleObj = familyParamToStyle(familyParam)
            return {
                url,
                styleObj,
                name: family,
            }
        },
        prevFont () {
            this.fontIndex--
            if (this.fontIndex < 0) {
                this.fontIndex = this.fontConfigs.length - 1
            }
            this.updateFont(this.fontConfigs[this.fontIndex].url)
        },
        nextFont () {
            this.fontIndex = (this.fontIndex + 1) % this.fontConfigs.length
            this.updateFont(this.fontConfigs[this.fontIndex].url)
        },
        updateFont (url) {
            this.$emit('input', url)
        }
    }
}
</script>

<style lang="scss" scoped>
.select select {
    max-width: 175px;
}

.button {
    padding-left: 0.5rem;
    padding-right: 0.5rem;
}
</style>
